import {
  Column,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { PinConfig, SkeletonLoader } from '@toggle/design-system';
import { isElementScrollbarVisible, useElemSize } from '@toggle/helpers';
import { ColumnMeta } from '@toggle/toggle';
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { AssetOverviewDrawer } from '~/widgets/asset-overview-widget/AssetOverviewDrawer';
import { DraggableWatchlistRow } from '~/widgets/draggable-watchlist-row/DraggableWatchlistRow';
import { InsightsDrawer } from '~/widgets/insight-widget/components/insights-drawer/InsightsDrawer';

import { AssetsTableHeaderCell } from './assets-table-header-cell/AssetsTableHeaderCell';
import * as S from './AssetsTable.styles';
import { getColumns } from './get-columns';
import { EntityPlusPrice } from './useAssetsTableData';
import { useAssetsTableStore } from './useAssetsTableStore';

export interface AssetsTableProps {
  data: EntityPlusPrice[];
  columnsMeta: ColumnMeta[];
  listId: string;
  tab: string;
  isEditMode?: boolean;
  rowSelection?: Record<string, boolean>;
  onRowSelect?: (rowId: string, selected: boolean) => void;
  toggleAllRowsSelection?: (selected: boolean) => void;
  onRowOrderChange?: ({
    toIdx,
    fromIdx,
  }: {
    toIdx: number;
    fromIdx: number;
  }) => void;
}

export const AssetsTable = ({
  data,
  columnsMeta,
  tab,
  listId,
  isEditMode = false,
  rowSelection = {},
  onRowSelect = () => {},
  toggleAllRowsSelection = () => {},
  onRowOrderChange = () => {},
}: AssetsTableProps) => {
  //prevent translations lazy loading when insights are opened
  const { t } = useTranslation([
    'widget',
    'scenarioCommander',
    'scenario',
    'screener',
    'asset-overview',
  ]);
  const [selectedInsight, setSelectedInsight] = useState('');
  const [insightsDrawerOpen, setInsightsDrawerOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [showScrollShadow, setShowScrollShadow] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const intersectionRef = useRef<HTMLDivElement>(null);

  const { sortingState, reset } = useAssetsTableStore(state => ({
    sortingState: state.sortingState,
    reset: state.reset,
  }));
  useElemSize(containerRef);

  const columns = useMemo(
    () =>
      getColumns({
        t,
        selectedInsight,
        setSelectedInsight: id => {
          if (selectedInsight) {
            // `onClose` of the Tooltip component triggers a rerender that prevent onClick event to be executed
            // We manually slow down event to give some time to the click event
            setTimeout(() => setSelectedInsight(id), 120);
          } else {
            setSelectedInsight(id);
          }
        },
        setInsightsDrawerOpen,
        insightsDrawerOpen,
        columnsMeta,
        tab,
        isEditMode,
        toggleAllRowsSelection,
      }),
    [selectedInsight, columnsMeta, insightsDrawerOpen, isEditMode, rowSelection]
  );

  useEffect(() => {
    return reset;
  }, [listId]);

  // TO-DO: improve, see TECHDEBT-13
  useLayoutEffect(() => {
    if (!containerRef?.current || !intersectionRef?.current) {
      return undefined;
    }

    let options = {
      root: containerRef.current,
      rootMargin: '0px',
      threshold: [0, 1],
    };

    const handleIntersect: IntersectionObserverCallback = (entries, _) => {
      entries.forEach(entry => {
        if (entry.intersectionRatio < 1) {
          setShowScrollShadow(true);
        } else {
          setShowScrollShadow(false);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, options);
    observer.observe(intersectionRef.current);
  }, [containerRef]);

  const hasHorizontalScroll = isElementScrollbarVisible(containerRef);

  const getIsPinned = (column: Column<EntityPlusPrice>) =>
    hasHorizontalScroll && !!column.getIsPinned();

  const table = useReactTable({
    data,
    columns,
    defaultColumn: {
      size: 0, // Set default to smallest size so cell grows to fit min content width
    },
    enableRowSelection: true,
    state: {
      columnPinning: {
        left: ['rowSelected', 'entity', 'name'],
      },
      rowSelection,
      sorting: listId ? sortingState : undefined,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: data => data.tag,
  });

  const getPinConfig = (
    column: Column<EntityPlusPrice>
  ): PinConfig | undefined => {
    if (getIsPinned(column)) {
      return {
        left: column.getStart('left'),
        right: column.getStart('right'),
        isLastPinned: column.getIsLastColumn('left'),
        showShadow: showScrollShadow,
      };
    }
    return undefined;
  };

  const onClickAOInsight = (insightId: string) => {
    setSelectedInsight(insightId);
    setSelectedEntity('');
    setInsightsDrawerOpen(true);
  };

  const skeletonCell = (
    <SkeletonLoader
      areas={[
        {
          styles: {
            width: '100%',
            height: '20px',
            borderRadius: '8px',
          },
        },
      ]}
    />
  );

  return (
    <>
      <AssetOverviewDrawer
        entityTag={selectedEntity}
        isOpen={!!selectedEntity}
        closeDrawer={() => setSelectedEntity('')}
        onClickInsight={onClickAOInsight}
      />
      <InsightsDrawer
        insightId={selectedInsight}
        isOpen={insightsDrawerOpen}
        closeDrawer={() => {
          setInsightsDrawerOpen(false);
          setSelectedInsight('');
          setSelectedEntity('');
        }}
      />
      <S.Wrapper data-testid="assets-table" ref={containerRef}>
        <div className="intersection" ref={intersectionRef} />
        <S.TableWrapper>
          <S.StyledTanStackTableRoot>
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <AssetsTableHeaderCell
                      key={header.id}
                      header={header}
                      pinConfig={getPinConfig(header.column)}
                    />
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => {
                const isRowLoading = row.original.isLoading;
                const shouldHideRow = isRowLoading && row.index > 2;

                if (shouldHideRow) {
                  return null;
                }

                const cells = row.getVisibleCells().map(cell => (
                  <S.StyledTableCell
                    key={cell.id}
                    isNumber={!cell.column.getIsPinned()}
                    alignment={cell.column.getIsPinned() ? 'left' : 'end'}
                    pinConfig={getPinConfig(cell.column)}
                  >
                    {isRowLoading
                      ? skeletonCell
                      : flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                  </S.StyledTableCell>
                ));

                return isEditMode ? (
                  <DraggableWatchlistRow
                    key={row.id}
                    row={row}
                    onDrop={onRowOrderChange}
                    data={data}
                    afterNode={
                      <S.StyledCheckBox
                        name={`watchlist-${row.id}`}
                        label=""
                        checked={row.getIsSelected()}
                        onChange={() =>
                          onRowSelect(row.id, !row.getIsSelected())
                        }
                      />
                    }
                  >
                    {cells}
                  </DraggableWatchlistRow>
                ) : (
                  <tr
                    key={row.id}
                    onClick={() =>
                      setSelectedEntity(row.original.asset?.tag ?? '')
                    }
                  >
                    {cells}
                  </tr>
                );
              })}
            </tbody>
          </S.StyledTanStackTableRoot>
        </S.TableWrapper>
      </S.Wrapper>
    </>
  );
};
